import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Box, Card, CardContent, Chip, Stack, TextField, Typography } from "@mui/material";
import { SecurityOutlined } from "@mui/icons-material";

import MainText from "../../Components/MainText";
import { useAuth } from "../../context/AuthContext";
import { RBAC } from "../../services/api";

const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.results)) return value.results;
  return [];
};

const exampleFor = (permission) => {
  if (permission?.example_es) return permission.example_es;
  const name = String(permission?.friendly_name || permission?.label_es || permission?.name || "permiso").toLowerCase();
  if (name.includes("caja")) return "Ejemplo: consultar, abrir, cerrar o cuadrar una sesion de caja segun el permiso asignado.";
  if (name.includes("venta")) return "Ejemplo: crear, cobrar, anular o revisar ventas segun el permiso asignado.";
  if (name.includes("grooming")) return "Ejemplo: gestionar recepcion, ordenes, servicios o cobros grooming segun el permiso asignado.";
  if (name.includes("usuario") || name.includes("rol")) return "Ejemplo: administrar accesos del personal segun el permiso asignado.";
  return "Ejemplo: habilita esta accion solo para usuarios que necesitan realizarla en operacion.";
};

function PermissionGuidePage() {
  const { isSuperuser } = useAuth();
  const [rows, setRows] = useState([]);
  const [query, setQuery] = useState("");
  const [error, setError] = useState(false);

  const loadRows = useCallback(async () => {
    try {
      const data = await RBAC.permissions();
      setRows(toArray(data));
      setError(false);
    } catch {
      setRows([]);
      setError(true);
    }
  }, []);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const filteredRows = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return rows;
    return rows.filter((row) =>
      `${row.friendly_name || ""} ${row.label_es || ""} ${row.name || ""} ${row.description || ""} ${row.code || ""}`
        .toLowerCase()
        .includes(normalized)
    );
  }, [query, rows]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <MainText title="Guia de permisos" subtitle="Consulta de permisos disponibles. Sin CRUD." icon={SecurityOutlined} />
      {error ? <Alert severity="error">No se pudo cargar la guia de permisos.</Alert> : null}
      <TextField
        label="Buscar"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        size="small"
        sx={{ maxWidth: 420 }}
      />
      <Stack spacing={1}>
        {filteredRows.map((permission) => (
          <Card key={permission.id || permission.code} variant="outlined">
            <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
              <Stack spacing={0.5}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    {permission.friendly_name || permission.label_es || permission.name || "Permiso"}
                  </Typography>
                  {isSuperuser ? <Chip size="small" variant="outlined" label={permission.code} /> : null}
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  {permission.caption || permission.description || "Sin descripcion."}
                </Typography>
                <Typography variant="body2">{exampleFor(permission)}</Typography>
              </Stack>
            </CardContent>
          </Card>
        ))}
      </Stack>
    </Box>
  );
}

export default PermissionGuidePage;
